import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { paragonCrmProviderFor } from "components/integrations/managed_integrations/crmProviders";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import { appRoutes } from "lib/appRoutes";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useFieldMappings } from "lib/hooks/api/integrations/useFieldMappings";
import { useParagonCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import { useListOpportunityEntitiesProvider } from "lib/hooks/api/integrations/useListOpportunityEntitiesProvider";
import useParagonUser from "lib/hooks/api/integrations/useParagonUser";
import { MAPPABLE_RECORD_KINDS } from "lib/integrations/paragonCrm/fieldCompatibility";
import { CrmRecordKind } from "lib/integrations/paragonCrm/recordFields";
import { TrackingProvider } from "lib/tracking";
import FieldMappingSection from "./FieldMappingSection";

/**
 * Configures what Stotles writes into the customer's CRM, one section per kind of record.
 *
 * Replaces the field mapping in Paragon's Connect Portal: the mapping is stored here, so the form
 * can offer only the fields a record can actually be written to and refuse one the CRM would
 * reject at push time.
 */
export default function FieldMappingPage(): JSX.Element {
  const { user } = useParagonUser();
  const { data: install, isInitialLoading: isLoadingInstall } = useParagonCrmProvider();

  // The install says which CRM to configure; Paragon's own state says whether that connection is
  // still live. A CRM we run OAuth for has no entry here, so it resolves to no provider and the
  // page offers nothing rather than a form built against a schema it cannot read.
  const provider = paragonCrmProviderFor(install);
  const isConnected = !!provider && !!user?.integrations?.[provider.integrationKey]?.enabled;

  const {
    data: entityList,
    isInitialLoading: isLoadingEntities,
    error: entitiesError,
  } = useListOpportunityEntitiesProvider({ enabled: isConnected });
  const {
    data: mappings,
    isInitialLoading: isLoadingMappings,
    error: mappingsError,
  } = useFieldMappings(isConnected);

  if (!user || isLoadingInstall) {
    return <CentredSpinner />;
  }

  if (!isConnected || !provider) {
    return (
      <Wrapper>
        <EmptyState
          size="large"
          heading="No CRM connected"
          description="Connect your CRM before choosing where Stotles writes to it."
          type="loadError"
        />
      </Wrapper>
    );
  }

  if (isLoadingEntities || isLoadingMappings) {
    return <CentredSpinner />;
  }

  // Refusing the form outright rather than falling through to an empty one: with the saved mapping
  // unread, every section would render blank and a save would replace a working mapping with it.
  if (entitiesError || mappingsError) {
    return (
      <Wrapper>
        <EmptyState
          size="large"
          heading="Could not load your CRM configuration"
          description="Stotles could not reach your CRM. Please try again, and contact us if this persists."
          type="loadError"
        />
      </Wrapper>
    );
  }

  const entities = entityList?.entities ?? [];
  if (entities.length === 0) {
    return (
      <Wrapper>
        <EmptyState
          size="large"
          heading="No objects to map to"
          description="Your CRM did not offer any objects Stotles can push to. Please contact us if this persists."
          type="loadError"
        />
      </Wrapper>
    );
  }

  const savedFor = (kind: CrmRecordKind) =>
    mappings?.mappings.find((mapping) => mapping.stotlesObjectType === kind.toUpperCase());

  return (
    <TrackingProvider
      data={{
        "Page source": "Integration Configuration",
        "Connected provider": provider.providerType,
      }}
    >
      <Page>
        <Flex column gap={8} alignItems="flex-start">
          <Button as="a" buttonType="tertiary" icon="arrowLeft" {...appRoutes.integrations}>
            Back
          </Button>
          <Text h1>Configure your CRM</Text>
          <Text description>
            Choose which field in your CRM each piece of Stotles data is written to. Only fields
            that can hold the data are offered.
          </Text>
        </Flex>

        {MAPPABLE_RECORD_KINDS.map((kind) => (
          <FieldMappingSection
            key={kind}
            kind={kind}
            provider={provider}
            entities={entities}
            saved={savedFor(kind)}
          />
        ))}
      </Page>
    </TrackingProvider>
  );
}

const Page = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 32,
  padding: 32,
  maxWidth: 1000,
});

const Wrapper = styled(Flex)({
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
});
